import React from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ComplianceTab() {
  const [cfg, setCfg] = React.useState(null);
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [backfill, setBackfill] = React.useState(null);
  const [backfillBusy, setBackfillBusy] = React.useState(false);

  const load = async () => {
    const r = await api.get("/settings/compliance");
    setCfg(r.data);
    setForm({
      gstin: r.data.gstin || "",
      nic_einvoice_user: r.data.nic_einvoice_user || "",
      nic_einvoice_pwd: r.data.nic_einvoice_pwd_masked || "",
      nic_einvoice_client_id: r.data.nic_einvoice_client_id || "",
      nic_einvoice_client_secret: r.data.nic_einvoice_client_secret_masked || "",
      nic_ewb_user: r.data.nic_ewb_user || "",
      nic_ewb_pwd: r.data.nic_ewb_pwd_masked || "",
      nic_ewb_client_id: r.data.nic_ewb_client_id || "",
      nic_ewb_client_secret: r.data.nic_ewb_client_secret_masked || "",
      nic_public_key: "",
      nic_mode: r.data.nic_mode || "sandbox",
    });
  };
  const loadBackfill = React.useCallback(async () => {
    try { const r = await api.get("/einvoice/backfill/status"); setBackfill(r.data); }
    catch { setBackfill(null); }
  }, []);
  React.useEffect(() => { load(); loadBackfill(); }, [loadBackfill]);
  // Poll running backfill every 2s — loadBackfill is stable via useCallback
  React.useEffect(() => {
    if (backfill?.status !== "running" && backfill?.status !== "queued") return;
    const t = setInterval(loadBackfill, 2000);
    return () => clearInterval(t);
  }, [backfill?.status, loadBackfill]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      // Only send secrets when the user actually retyped (i.e. value doesn't contain the mask bullet)
      const payload = { ...form };
      for (const k of ["nic_einvoice_pwd", "nic_ewb_pwd", "nic_einvoice_client_secret", "nic_ewb_client_secret"]) {
        if (typeof payload[k] === "string" && payload[k].includes("•")) delete payload[k];
      }
      if (!payload.nic_public_key) delete payload.nic_public_key;
      await api.post("/settings/compliance", payload);
      toast.success("Compliance configuration saved (secrets encrypted at rest)");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const test = async (system) => {
    setTesting(true);
    try {
      const r = await api.post(`/settings/compliance/test?system=${system}`, {});
      if (r.data.ok) toast.success(`✓ ${r.data.message}`);
      else toast.error(`✗ ${r.data.message}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const startBackfill = async () => {
    if (!window.confirm("Kick off IRN backfill for ALL unblocked historic invoices? This calls the live NIC IRP for each one.")) return;
    setBackfillBusy(true);
    try {
      const r = await api.post("/einvoice/backfill", { limit: 500 });
      setBackfill(r.data);
      toast.success(`Backfill job started · ${r.data.id.slice(0, 8)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed to start"); }
    finally { setBackfillBusy(false); }
  };

  if (!cfg) return <div className="text-muted-foreground p-4">Loading…</div>;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="compliance-panel">
      {/* Status banner */}
      <div className={`card-sharp p-4 flex items-center gap-3 ${cfg.configured ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`}>
        <div className={`h-2 w-2 rounded-full ${cfg.configured ? "bg-emerald-500" : "bg-amber-500"}`}></div>
        <div className="flex-1">
          <div className="text-sm font-medium">
            {cfg.configured ? `Direct NIC connection · ${cfg.nic_mode === "production" ? "Production" : "Sandbox"}` : "Not configured yet"}
          </div>
          <div className="text-xs text-muted-foreground">
            {cfg.configured ? `Last updated ${(cfg.updated_at || "").slice(0, 16)}` : "Fill in your company's own NIC API login below to create IRNs and E-Way Bills directly (no GSP)."}
          </div>
        </div>
        {cfg.configured && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => test("einv")} disabled={testing} className="rounded-sm" data-testid="test-nic-einv">
              {testing ? "Testing…" : "Test e-Invoice login"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => test("ewb")} disabled={testing} className="rounded-sm" data-testid="test-nic-ewb">
              Test E-Way Bill login
            </Button>
          </div>
        )}
      </div>

      {/* GSTIN */}
      <div className="card-sharp p-5 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">GSTIN</div>
        <div><Label>Company GSTIN (as registered with NIC)</Label>
          <Input value={form.gstin} onChange={(e) => set({ gstin: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono max-w-sm" placeholder="27AABCZ1234A1Z5" data-testid="compl-gstin" />
        </div>
      </div>

      {/* NIC E-Invoice */}
      <div className="card-sharp p-5 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">NIC E-Invoice API (direct)</div>
        <div className="text-xs text-muted-foreground">Create these at <span className="font-mono">einvoice1.gst.gov.in → API Registration → Direct integration</span>. NIC emails the Client ID and Secret.</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>API username</Label><Input value={form.nic_einvoice_user} onChange={(e) => set({ nic_einvoice_user: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="compl-einv-user" /></div>
          <div><Label>API password</Label><Input type="password" value={form.nic_einvoice_pwd} onChange={(e) => set({ nic_einvoice_pwd: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="compl-einv-pwd" /></div>
          <div><Label>Client ID</Label><Input value={form.nic_einvoice_client_id} onChange={(e) => set({ nic_einvoice_client_id: e.target.value.trim() })} className="rounded-sm mt-1 font-mono" data-testid="compl-einv-client-id" /></div>
          <div><Label>Client secret</Label><Input type="password" value={form.nic_einvoice_client_secret} onChange={(e) => set({ nic_einvoice_client_secret: e.target.value.trim() })} className="rounded-sm mt-1 font-mono" data-testid="compl-einv-client-secret" /></div>
        </div>
      </div>

      {/* NIC E-Way Bill */}
      <div className="card-sharp p-5 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">NIC E-Way Bill API (direct)</div>
        <div className="text-xs text-muted-foreground">Create these at <span className="font-mono">ewaybillgst.gov.in → Registration → For API (direct)</span>.</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>API username</Label><Input value={form.nic_ewb_user} onChange={(e) => set({ nic_ewb_user: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="compl-ewb-user" /></div>
          <div><Label>API password</Label><Input type="password" value={form.nic_ewb_pwd} onChange={(e) => set({ nic_ewb_pwd: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="compl-ewb-pwd" /></div>
          <div><Label>Client ID</Label><Input value={form.nic_ewb_client_id} onChange={(e) => set({ nic_ewb_client_id: e.target.value.trim() })} className="rounded-sm mt-1 font-mono" data-testid="compl-ewb-client-id" /></div>
          <div><Label>Client secret</Label><Input type="password" value={form.nic_ewb_client_secret} onChange={(e) => set({ nic_ewb_client_secret: e.target.value.trim() })} className="rounded-sm mt-1 font-mono" data-testid="compl-ewb-client-secret" /></div>
        </div>
      </div>

      {/* NIC public key + environment */}
      <div className="card-sharp p-5 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">NIC public key &amp; environment</div>
        <div className="text-xs text-muted-foreground">Download the public key from <span className="font-mono">einvoice.gst.gov.in/publickeys</span> (sandbox and production keys differ) and paste it here. {cfg.nic_public_key_set ? "A key is saved — paste a new one only to replace it." : "No key saved yet."}</div>
        <textarea value={form.nic_public_key} onChange={(e) => set({ nic_public_key: e.target.value })} rows={4} placeholder="-----BEGIN PUBLIC KEY-----" className="w-full border rounded-sm p-2 font-mono text-xs bg-background" data-testid="compl-nic-public-key" />
        <div className="max-w-xs"><Label>Environment</Label>
          <Select value={form.nic_mode} onValueChange={(v) => set({ nic_mode: v })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="compl-nic-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sandbox">Sandbox (test)</SelectItem>
              <SelectItem value="production">Production</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={load} className="rounded-sm" data-testid="compl-reset">Reset</Button>
        <Button onClick={save} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="compl-save">
          {busy ? "Saving…" : "Save (encrypt at rest)"}
        </Button>
      </div>

      {/* Backfill panel */}
      <div className="card-sharp p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Bulk IRN backfill</div>
            <div className="text-xs text-muted-foreground mt-1">Generate IRN + signed QR for every historic invoice that doesn't have one yet.</div>
          </div>
          <Button onClick={startBackfill} disabled={backfillBusy || backfill?.status === "running"} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="start-backfill">
            {backfill?.status === "running" ? "Running…" : "Start backfill"}
          </Button>
        </div>
        {backfill && backfill.status && backfill.status !== "none" && (
          <div className="bg-muted/40 rounded-sm p-3 text-xs space-y-2" data-testid="backfill-status">
            <div className="flex items-center gap-3">
              <span className={`inline-block h-2 w-2 rounded-full ${backfill.status === "running" ? "bg-blue-500 animate-pulse" : backfill.status === "finished" ? "bg-emerald-500" : "bg-muted-foreground"}`}></span>
              <span className="font-mono uppercase">{backfill.status}</span>
              <span className="text-muted-foreground">·</span>
              <span>{backfill.processed || 0} / {backfill.total || 0} processed</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-emerald-600">✓ {backfill.succeeded || 0}</span>
              <span className="text-red-600">✗ {backfill.failed || 0}</span>
            </div>
            {backfill.total > 0 && (
              <div className="h-1.5 bg-muted rounded-sm overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${Math.min(100, (backfill.processed / backfill.total) * 100)}%` }}></div>
              </div>
            )}
            {(backfill.errors || []).length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-red-600">{backfill.errors.length} errors (first 25 shown)</summary>
                <ul className="mt-2 space-y-1 font-mono text-[11px]">
                  {backfill.errors.slice(0, 25).map((e, i) => (
                    <li key={i}>· <span className="text-accent">{e.invoice_number || e.invoice_id?.slice(0, 8)}</span> — {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="text-[10px] text-muted-foreground italic">
        🔒 Passwords &amp; API secrets are AES-128-CBC encrypted before hitting MongoDB. Only the last 4 characters are ever exposed to the UI.
      </div>

      <OnboardingWizard />
      <ResendPanel />
      <DigestPanel />
      <TDS26QPanel />
      <BulkCancelPanel />
    </div>
  );
}

// Iter-144 · Form 26Q Draft — aggregates TDS Payable ledger credits from
// every posted voucher (bills, JVs, bank-reconcile create-voucher). Ready
// for the CA to compare against TRACES / challan-generation.
function TDS26QPanel() {
  const [fromDate, setFromDate] = React.useState("");
  const [toDate, setToDate] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [dlBusy, setDlBusy] = React.useState(false);
  const [data, setData] = React.useState(null);

  // Default to current quarter
  React.useEffect(() => {
    if (fromDate || toDate) return;
    const t = new Date();
    const q = Math.floor(t.getMonth() / 3);
    const qStart = new Date(t.getFullYear(), q * 3, 1);
    setFromDate(qStart.toISOString().slice(0, 10));
    setToDate(t.toISOString().slice(0, 10));
  }, [fromDate, toDate]);

  const preview = async () => {
    if (!fromDate || !toDate) { toast.error("Pick a date range"); return; }
    setBusy(true);
    try {
      const r = await api.get("/reports/tds-26q-draft", { params: { from_date: fromDate, to_date: toDate } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const download = async () => {
    if (!fromDate || !toDate) { toast.error("Pick a date range"); return; }
    setDlBusy(true);
    try {
      const r = await api.get("/reports/tds-26q-draft", {
        params: { from_date: fromDate, to_date: toDate, format: "csv" },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `TDS_26Q_Draft_${fromDate}_to_${toDate}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("26Q Draft CSV downloaded — hand it to your CA for TRACES.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    finally { setDlBusy(false); }
  };

  const fmt = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="card-sharp p-5 space-y-3" data-testid="tds26q-panel">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">📄 TDS Form 26Q Draft</div>
        <div className="text-xs text-muted-foreground mt-1">
          Aggregates every <b>TDS Payable</b> ledger credit from posted vouchers in the period — including manual JVs
          and TDS deducted inside Bank Reconcile → Create Voucher. Not a final return — download and reconcile with your CA before filing on TRACES.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <div><Label>From date</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-sm mt-1" data-testid="tds26q-from" />
        </div>
        <div><Label>To date</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-sm mt-1" data-testid="tds26q-to" />
        </div>
        <div>
          <Button variant="outline" onClick={preview} disabled={busy} className="w-full rounded-sm" data-testid="tds26q-preview">
            {busy ? "Loading…" : "Preview"}
          </Button>
        </div>
        <div>
          <Button onClick={download} disabled={dlBusy} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="tds26q-download">
            {dlBusy ? "…" : "Download 26Q CSV"}
          </Button>
        </div>
      </div>

      {data && (
        <div className="space-y-3 pt-2 border-t" data-testid="tds26q-preview-body">
          {/* Headline totals */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
              <div className="text-xl font-bold text-slate-900" data-testid="tds26q-count">{data.totals.count}</div>
              <div className="text-[10px] uppercase text-slate-600">Transactions</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
              <div className="text-xl font-bold text-blue-800 tabular-nums" data-testid="tds26q-gross">{fmt(data.totals.amount_credited)}</div>
              <div className="text-[10px] uppercase text-blue-800">Amount credited</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
              <div className="text-xl font-bold text-emerald-800 tabular-nums" data-testid="tds26q-total">{fmt(data.totals.tds_deducted)}</div>
              <div className="text-[10px] uppercase text-emerald-800">TDS deducted</div>
            </div>
          </div>

          {/* Section-wise rollup */}
          {data.by_section?.length > 0 && (
            <div className="border rounded-sm overflow-hidden">
              <div className="bg-muted/30 px-3 py-1.5 text-[10px] font-mono uppercase text-muted-foreground">By section</div>
              <table className="w-full text-xs">
                <thead className="bg-muted/10 text-[10px]">
                  <tr>
                    <th className="text-left px-3 py-1.5">Section</th>
                    <th className="text-right px-3 py-1.5">Txns</th>
                    <th className="text-right px-3 py-1.5">Amount credited</th>
                    <th className="text-right px-3 py-1.5">TDS deducted</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_section.map((s) => (
                    <tr key={s.section} className="border-t" data-testid={`tds26q-sec-${s.section}`}>
                      <td className="px-3 py-1.5 font-mono">{s.section}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{s.count}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmt(s.amount_credited)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt(s.tds_deducted)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Row-level preview (first 10) */}
          {data.rows?.length > 0 && (
            <div className="border rounded-sm overflow-hidden">
              <div className="bg-muted/30 px-3 py-1.5 text-[10px] font-mono uppercase text-muted-foreground">Line items — first 10 of {data.rows.length}</div>
              <table className="w-full text-xs">
                <thead className="bg-muted/10 text-[10px]">
                  <tr>
                    <th className="text-left px-3 py-1.5">Section</th>
                    <th className="text-left px-3 py-1.5">Deductee</th>
                    <th className="text-left px-3 py-1.5">PAN</th>
                    <th className="text-left px-3 py-1.5">Voucher #</th>
                    <th className="text-left px-3 py-1.5">Date</th>
                    <th className="text-right px-3 py-1.5">Gross ₹</th>
                    <th className="text-right px-3 py-1.5">Rate %</th>
                    <th className="text-right px-3 py-1.5">TDS ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.slice(0, 10).map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-1.5 font-mono">{r.section || "—"}</td>
                      <td className="px-3 py-1.5">{r.deductee_name || "—"}</td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">{r.deductee_pan || "—"}</td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">{r.voucher_no}</td>
                      <td className="px-3 py-1.5 text-[10px]">{r.date}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.amount_credited)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.tds_rate}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt(r.tds_deducted)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.totals.count === 0 && (
            <div className="text-center text-muted-foreground text-xs py-4">
              No TDS deductions found in this range. Post a voucher that credits a <b>TDS Payable</b> ledger to see it here.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** EmailSenderPanel — zero-vendor UX.
 *  Tenants NEVER see any API key. Platform-level provider (managed by Emergent
 *  ops) is used silently. Tenants only enter:
 *    1. Their sending domain (e.g. acme.com)
 *    2. The "From" address they want on outgoing invoices/reminders (e.g. billing@acme.com)
 *  UI shows the DNS records to add + a one-click Verify. */
function ResendPanel() {
  const [cfg, setCfg] = React.useState(null);
  const [domains, setDomains] = React.useState([]);
  const [active, setActive] = React.useState({});
  const [newDomain, setNewDomain] = React.useState("");
  const [fromEmail, setFromEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testRecipient, setTestRecipient] = React.useState("");
  const [platformLive, setPlatformLive] = React.useState(true);
  const [reason, setReason] = React.useState("");

  const load = React.useCallback(async () => {
    const c = await api.get("/settings/compliance");
    setCfg(c.data);
    setTestRecipient((cur) => cur || c.data.digest_recipient || "");
    try {
      const d = await api.get("/email/domains");
      setDomains(d.data.domains || []);
      setActive(d.data.active || {});
      setFromEmail(d.data.active?.verified_sender_email || "");
      setPlatformLive(d.data.ok !== false);
      setReason(d.data.reason || "");
    } catch (e) {
      setPlatformLive(false);
      setReason(e?.response?.data?.detail || "Email service unavailable");
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const addDomain = async () => {
    const n = newDomain.trim().toLowerCase();
    if (!n.match(/^[a-z0-9.-]+\.[a-z]{2,}$/)) return toast.error("Enter a valid domain (e.g. acme.com)");
    setBusy(true);
    try {
      await api.post("/email/domains", { name: n });
      toast.success(`Domain ${n} added — copy the DNS records below into your registrar`);
      setNewDomain("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  const verifyDomain = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/email/domains/${id}/verify`);
      const status = r.data.domain?.status || "pending";
      if (status === "verified") toast.success("✓ Domain verified — you can now activate your From address");
      else toast.info(`Status: ${status} — DNS records may still be propagating (retry in a few minutes)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
    finally { setBusy(false); }
  };
  const removeDomain = async (id) => {
    if (!window.confirm("Remove this domain from your account?")) return;
    setBusy(true);
    try { await api.delete(`/email/domains/${id}`); toast.success("Domain removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };
  const activate = async () => {
    const em = fromEmail.trim().toLowerCase();
    if (!em.includes("@")) return toast.error("Enter your full From address (e.g. billing@acme.com)");
    setBusy(true);
    try {
      await api.post("/email/domains/activate", { verified_sender_email: em });
      toast.success(`✓ Outgoing email will now be sent from ${em}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Activation failed"); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testRecipient?.includes("@")) return toast.error("Enter a recipient email first");
    setTesting(true);
    try {
      const r = await api.post("/email/test-send", { recipient: testRecipient });
      if (r.data.ok) toast.success(`✓ Test email sent to ${testRecipient}`);
      else toast.error(`✗ ${r.data.error || "Send failed"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setTesting(false); }
  };

  if (!cfg) return null;

  return (
    <div className={`card-sharp p-5 space-y-4 ${active.verified_sender_email ? "border-emerald-300" : "border-slate-200"}`} data-testid="resend-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-2">
            Outbound Email
            {active.verified_sender_email
              ? <span className="text-emerald-700 text-[9px] bg-emerald-100 px-1.5 py-0.5 rounded-sm">LIVE · {active.verified_sender_email}</span>
              : platformLive
                ? <span className="text-amber-700 text-[9px] bg-amber-100 px-1.5 py-0.5 rounded-sm">SETUP NEEDED</span>
                : <span className="text-slate-700 text-[9px] bg-slate-100 px-1.5 py-0.5 rounded-sm">PROVISIONING</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Send invoices, payment reminders and compliance digests from <b>your own domain</b>. No API keys — just add a domain, drop three DNS records, verify.
          </div>
        </div>
      </div>

      {!platformLive && (
        <div className="text-[11px] p-3 rounded-sm bg-slate-50 border border-slate-200 text-slate-700">
          The platform email service is being provisioned. Please contact support if this persists. <span className="font-mono text-[10px] text-slate-500">({reason || "no platform key"})</span>
        </div>
      )}

      {platformLive && (
        <>
          {/* Step 1: add a domain */}
          <div className="rounded-sm border border-slate-200 p-3 space-y-2">
            <div className="text-[10px] font-mono uppercase text-slate-500">Step 1 · Add your sending domain</div>
            <div className="flex items-end gap-2">
              <div className="flex-1"><Label>Domain</Label>
                <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addDomain()}
                  className="rounded-sm mt-1 font-mono" placeholder="acme.com" data-testid="email-domain-input" />
              </div>
              <Button onClick={addDomain} disabled={busy || !newDomain} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="email-domain-add">
                {busy ? "…" : "Add domain"}
              </Button>
            </div>
          </div>

          {/* Step 2: show DNS records per domain */}
          {domains.length > 0 && (
            <div className="rounded-sm border border-slate-200 p-3 space-y-3">
              <div className="text-[10px] font-mono uppercase text-slate-500">Step 2 · Add these DNS records at your registrar</div>
              {domains.map((d) => (
                <div key={d.id} className="rounded-sm bg-slate-50 p-3 border border-slate-200" data-testid={`email-domain-${d.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-sm">
                      <b>{d.name}</b>
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-sm ${d.status === "verified" ? "bg-emerald-100 text-emerald-700" : d.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                        {(d.status || "pending").toUpperCase()}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => verifyDomain(d.id)} disabled={busy} className="rounded-sm text-xs" data-testid={`email-domain-verify-${d.id}`}>Re-check DNS</Button>
                      <Button size="sm" variant="ghost" onClick={() => removeDomain(d.id)} disabled={busy} className="rounded-sm text-xs text-red-600 hover:text-red-700">Remove</Button>
                    </div>
                  </div>
                  {(d.records || []).length > 0 && (
                    <table className="w-full text-[11px] font-mono">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="text-left py-1">Type</th>
                          <th className="text-left py-1">Name</th>
                          <th className="text-left py-1">Value</th>
                          <th className="text-left py-1"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(d.records || []).map((rec, i) => (
                          <tr key={i}>
                            <td className="py-1 pr-2">{rec.type}</td>
                            <td className="py-1 pr-2 max-w-[220px] truncate" title={rec.name}>{rec.name}</td>
                            <td className="py-1 pr-2 max-w-[300px] truncate" title={rec.value}>{rec.value}</td>
                            <td className="py-1"><button className="text-accent hover:underline text-[10px]"
                              onClick={() => { navigator.clipboard?.writeText(rec.value || ""); toast.success("Copied"); }}>Copy</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
              <div className="text-[10px] text-muted-foreground italic">
                DNS records typically activate in 5-15 minutes. Click <b>Re-check DNS</b> after adding them at your registrar (GoDaddy / Cloudflare / Route53 / Namecheap etc.).
              </div>
            </div>
          )}

          {/* Step 3: pick From address once a domain is verified */}
          <div className="rounded-sm border border-slate-200 p-3 space-y-2">
            <div className="text-[10px] font-mono uppercase text-slate-500">Step 3 · Set your From address</div>
            <div className="flex items-end gap-2">
              <div className="flex-1"><Label>From email</Label>
                <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && activate()}
                  className="rounded-sm mt-1 font-mono" placeholder="billing@acme.com" data-testid="email-from-input" />
              </div>
              <Button onClick={activate} disabled={busy || !fromEmail} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="email-from-save">
                {busy ? "…" : "Activate"}
              </Button>
            </div>
            <div className="text-[10px] text-muted-foreground italic">
              Domain must be <b>verified</b> before activation. All outgoing invoices, payment reminders and compliance digests will use this address.
            </div>
          </div>

          {/* Digest recipient + Test send */}
          <div className="rounded-sm border border-slate-200 p-3 space-y-2">
            <div className="text-[10px] font-mono uppercase text-slate-500">Optional · Test &amp; digest recipient</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Default digest recipient (CFO email)</Label>
                <Input type="email" value={cfg.digest_recipient || ""}
                  onChange={async (e) => {
                    const v = e.target.value.trim();
                    setCfg((c) => ({ ...c, digest_recipient: v }));
                  }}
                  onBlur={async (e) => {
                    try { await api.post("/settings/compliance", { digest_recipient: e.target.value.trim() }); }
                    catch (err) { console.warn("[Compliance] digest_recipient save failed:", err?.response?.data?.detail || err?.message); }
                  }}
                  className="rounded-sm mt-1 font-mono" placeholder="cfo@acme.com" data-testid="digest-default-to" />
              </div>
              <div><Label>Send a test email to</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="email" value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} className="rounded-sm font-mono" placeholder="your@email.com" data-testid="resend-test-to" />
                  <Button variant="outline" onClick={sendTest} disabled={testing || !active.verified_sender_email} className="rounded-sm" data-testid="resend-test-send">
                    {testing ? "…" : "Send"}
                  </Button>
                </div>
                {!active.verified_sender_email && <div className="text-[10px] text-amber-700 mt-1">Activate a From address first</div>}
              </div>
            </div>
          </div>

          <div className="text-[10px] text-muted-foreground italic">
            🔒 The platform manages the email provider centrally. You never handle API keys — just your own domain &amp; sender identity.
          </div>
        </>
      )}
    </div>
  );
}

function OnboardingWizard() {
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState(0);
  const steps = [
    { title: "1 · Register on GSTN E-Invoice portal", url: "https://einvoice1.gst.gov.in",
      mockup: "einvoice",
      body: [
        "Log into einvoice1.gst.gov.in with your GSTIN + GST-portal password.",
        "Navigate to Registration → API Registration → Through GSP.",
        "Select GSP name Sandbox from the dropdown (registered GSP ID auto-fills).",
        "Enter a fresh Username + Password specifically for E-Invoice API — this is NOT your GST portal password.",
        "Verify the OTP sent to your registered mobile.",
        "Note these credentials — you'll paste them into the E-Invoice sub-user card above."
      ] },
    { title: "2 · Register on GSTN E-Way Bill portal", url: "https://ewaybillgst.gov.in",
      mockup: "ewb",
      body: [
        "Log into ewaybillgst.gov.in with the same GSTIN.",
        "Navigate to Registration → For GSP.",
        "Select Sandbox as your GSP.",
        "Create a separate Username + Password for E-Way Bill API. Verify OTP.",
        "This is DIFFERENT from the E-Invoice sub-user above — the GSTN keeps them isolated."
      ] },
    { title: "3 · Create Sandbox console account", url: "https://console.sandbox.co.in",
      mockup: "sandbox",
      body: [
        "Sign up at console.sandbox.co.in with your business email.",
        "Enable GST Compliance product → E-Invoice + E-Way Bill.",
        "In the Sandbox dashboard, add your GSTIN and paste BOTH sub-user credentials from steps 1 & 2.",
        "Copy the generated API Key (key_test_… or key_live_…) and API Secret (secret_…)."
      ] },
    { title: "4 · Paste everything into Zenith 360", url: null,
      mockup: "zenith",
      body: [
        "Return here and fill the GSTIN, NIC E-Invoice, NIC E-Way Bill, and Sandbox GSP cards above.",
        "Click Save (encrypt at rest) — secrets are Fernet-encrypted before hitting MongoDB.",
        "Hit Test connection to verify the round-trip with Sandbox.",
        "Now every Sales invoice will show Generate IRN and Generate E-Way Bill live-action buttons."
      ] },
  ];
  return (
    <div className="card-sharp p-5 space-y-3" data-testid="onboarding-wizard">
      <button className="w-full flex items-center justify-between text-left" onClick={() => setOpen(!open)} data-testid="wizard-toggle">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">🎓 New to E-Invoice? Onboarding wizard</div>
          <div className="text-xs text-muted-foreground mt-1">4 steps · roughly 20 minutes across three government portals · with UI mockups</div>
        </div>
        <span className="text-accent text-sm">{open ? "− Collapse" : "+ Expand"}</span>
      </button>
      {open && (
        <div className="grid grid-cols-4 gap-2 mt-3">
          {steps.map((s, i) => (
            <button key={i} onClick={() => setStep(i)}
              className={`text-left px-3 py-2 rounded-sm text-[11px] border ${step === i ? "border-accent bg-accent/10 text-accent" : "border-muted bg-muted/30"}`}
              data-testid={`wizard-step-${i}`}>
              <div className="font-mono uppercase text-[10px] opacity-70">Step {i + 1}</div>
              <div className="font-medium leading-tight mt-0.5">{s.title.split("·")[1]?.trim()}</div>
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-4 bg-muted/30 rounded-sm border-l-4 border-accent overflow-hidden">
          <div className="grid grid-cols-2">
            <div className="p-4">
              <div className="text-sm font-semibold">{steps[step].title}</div>
              {steps[step].url && (
                <a href={steps[step].url} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline block mt-1">
                  → Open {steps[step].url} in a new tab
                </a>
              )}
              <ol className="mt-3 space-y-2 text-xs list-decimal pl-4">
                {steps[step].body.map((b, i) => <li key={i}>{b}</li>)}
              </ol>
              <div className="flex justify-between mt-4">
                <Button variant="outline" size="sm" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="rounded-sm">← Previous</Button>
                {step < steps.length - 1 ? (
                  <Button size="sm" onClick={() => setStep(step + 1)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="wizard-next">Next →</Button>
                ) : (
                  <Button size="sm" onClick={() => setOpen(false)} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wizard-done">✓ Done</Button>
                )}
              </div>
            </div>
            <div className="bg-slate-100 border-l border-muted p-3 flex items-center justify-center">
              <PortalMockup kind={steps[step].mockup} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// SVG mockup of each portal — labels the exact widget the user must click
function PortalMockup({ kind }) {
  const shell = (headerBg, headerTxt, subTxt, chrome) => (
    <div className="w-full bg-white border border-slate-300 rounded-sm shadow-sm overflow-hidden font-sans" data-testid={`portal-mockup-${kind}`}>
      <div className="flex items-center gap-1 bg-slate-200 px-2 py-1 text-[9px] text-slate-600">
        <span className="inline-block w-2 h-2 rounded-full bg-red-400"></span>
        <span className="inline-block w-2 h-2 rounded-full bg-amber-400"></span>
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
        <span className="ml-2 truncate">{headerTxt}</span>
      </div>
      <div className={`px-3 py-2 text-white text-[11px] font-semibold ${headerBg}`}>
        {subTxt}
      </div>
      <div className="p-3 space-y-2">{chrome}</div>
    </div>
  );
  if (kind === "einvoice") {
    return shell("bg-blue-800", "einvoice1.gst.gov.in", "GST e-Invoice · IRP · Government of India", (
      <>
        <div className="grid grid-cols-4 gap-1 text-[9px] font-medium">
          {["Home", "Login", "Registration ▾", "Help"].map((t, i) => (
            <div key={t} className={`px-1.5 py-1 rounded-sm text-center ${i === 2 ? "bg-amber-100 border border-amber-500 relative" : "bg-slate-100 text-slate-600"}`}>
              {t}
              {i === 2 && <span className="absolute -top-2 -right-3 text-red-600 text-[14px]">⤴</span>}
            </div>
          ))}
        </div>
        <div className="bg-slate-50 border border-slate-200 p-1.5 rounded-sm">
          <div className="text-[9px] uppercase text-slate-500">Registration submenu</div>
          <div className="mt-1 space-y-0.5 text-[10px]">
            <div className="text-slate-500">▸ Portal Login</div>
            <div className="text-slate-500">▸ e-Invoice Enablement</div>
            <div className="bg-amber-100 border border-amber-500 px-1 py-0.5 rounded-sm relative">
              <b>▸ API Registration</b>
              <span className="absolute -right-4 top-0 text-red-600 text-[12px]">←</span>
            </div>
          </div>
        </div>
        <div className="border border-slate-200 rounded-sm p-1.5 text-[9px]">
          <div className="text-slate-500 mb-1">GSP</div>
          <div className="flex items-center justify-between border border-blue-400 bg-blue-50 rounded-sm px-1.5 py-1">
            <span className="font-mono">Sandbox ▾</span>
            <span className="text-red-600">✓</span>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-1">
            <input className="border border-slate-300 rounded-sm px-1 py-0.5" placeholder="Username" readOnly />
            <input className="border border-slate-300 rounded-sm px-1 py-0.5" placeholder="Password" readOnly />
          </div>
        </div>
      </>
    ));
  }
  if (kind === "ewb") {
    return shell("bg-emerald-800", "ewaybillgst.gov.in", "e-Way Bill System · GSTN · NIC", (
      <>
        <div className="grid grid-cols-4 gap-1 text-[9px] font-medium">
          {["Home", "Login", "Registration ▾", "Search"].map((t, i) => (
            <div key={t} className={`px-1.5 py-1 rounded-sm text-center ${i === 2 ? "bg-amber-100 border border-amber-500 relative" : "bg-slate-100 text-slate-600"}`}>
              {t}
              {i === 2 && <span className="absolute -top-2 -right-3 text-red-600 text-[14px]">⤴</span>}
            </div>
          ))}
        </div>
        <div className="bg-slate-50 border border-slate-200 p-1.5 rounded-sm space-y-0.5 text-[10px]">
          <div className="text-slate-500">▸ EWB Registration</div>
          <div className="text-slate-500">▸ Transporter Registration</div>
          <div className="bg-amber-100 border border-amber-500 px-1 py-0.5 rounded-sm relative">
            <b>▸ For GSP</b>
            <span className="absolute -right-4 top-0 text-red-600 text-[12px]">←</span>
          </div>
        </div>
        <div className="border border-slate-200 rounded-sm p-1.5 text-[9px] space-y-1">
          <div className="border border-emerald-400 bg-emerald-50 rounded-sm px-1.5 py-1 flex justify-between">
            <span className="font-mono">GSP: Sandbox ▾</span>
            <span className="text-red-600">✓</span>
          </div>
          <input className="border border-slate-300 rounded-sm px-1 py-0.5 w-full" placeholder="EWB API Username (separate from E-Invoice!)" readOnly />
          <input className="border border-slate-300 rounded-sm px-1 py-0.5 w-full" placeholder="EWB API Password" readOnly />
        </div>
      </>
    ));
  }
  if (kind === "sandbox") {
    return shell("bg-slate-900", "console.sandbox.co.in", "Sandbox · Developer Console", (
      <>
        <div className="flex gap-1 text-[9px]">
          <div className="px-1.5 py-1 bg-slate-100 rounded-sm">Dashboard</div>
          <div className="px-1.5 py-1 bg-amber-100 border border-amber-500 rounded-sm relative">
            <b>Products ▾</b>
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-red-600 text-[12px]">↓</span>
          </div>
          <div className="px-1.5 py-1 bg-slate-100 rounded-sm">API Keys</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-1.5 text-[9px]">
          <div className="grid grid-cols-2 gap-1">
            <div className="p-1 bg-white border border-slate-200 rounded-sm">🧾 KYC / Aadhaar</div>
            <div className="p-1 bg-white border border-slate-200 rounded-sm">🏦 Bank verify</div>
            <div className="p-1 bg-amber-100 border border-amber-500 rounded-sm relative">
              <b>🇮🇳 GST Compliance</b>
              <span className="absolute -right-4 top-0 text-red-600 text-[12px]">←</span>
            </div>
            <div className="p-1 bg-white border border-slate-200 rounded-sm">📊 Analytics</div>
          </div>
        </div>
        <div className="border border-slate-200 rounded-sm p-1.5 text-[9px] space-y-0.5">
          <div className="flex justify-between"><span className="text-slate-500">Toggle E-Invoice</span><span className="text-emerald-600 font-bold">● ON</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Toggle E-Way Bill</span><span className="text-emerald-600 font-bold">● ON</span></div>
          <div className="mt-1 border-t border-slate-200 pt-1">
            <div className="font-mono text-[8px] text-slate-600">API Key: <span className="text-emerald-700">key_live_f6868…</span></div>
            <div className="font-mono text-[8px] text-slate-600">Secret: <span className="text-emerald-700">secret_live_1acc4…</span></div>
          </div>
        </div>
      </>
    ));
  }
  // zenith
  return shell("bg-black", "zenith360.in · Settings", "// Zenith 360 · Compliance", (
    <>
      <div className="bg-emerald-50 border border-emerald-300 rounded-sm px-1.5 py-1 text-[9px] flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
        <span>Configured · Sandbox test mode</span>
      </div>
      <div className="space-y-1 text-[9px]">
        <div className="border border-slate-200 rounded-sm p-1">
          <div className="text-[8px] font-mono uppercase text-orange-600">GSTIN</div>
          <div className="font-mono">27AABCZ1234A1Z5</div>
        </div>
        <div className="border border-slate-200 rounded-sm p-1">
          <div className="text-[8px] font-mono uppercase text-orange-600">Sandbox GSP</div>
          <div className="font-mono">key_live_f68…</div>
          <div className="font-mono">•••••••••79a3</div>
        </div>
      </div>
      <div className="flex justify-end gap-1">
        <div className="px-1.5 py-0.5 bg-slate-100 rounded-sm text-[9px]">Test</div>
        <div className="px-1.5 py-0.5 bg-orange-500 text-white rounded-sm text-[9px] relative">
          Save
          <span className="absolute -right-4 top-1/2 -translate-y-1/2 text-red-600 text-[12px]">←</span>
        </div>
      </div>
    </>
  ));
}

function DigestPanel() {
  const [preview, setPreview] = React.useState(null);
  const [recipient, setRecipient] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    try { const r = await api.get("/einvoice/digest/preview"); setPreview(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };
  React.useEffect(() => { load(); }, []);
  const send = async () => {
    if (!recipient || !recipient.includes("@")) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      const r = await api.post("/einvoice/digest/send", { recipient });
      if (r.data.sent) toast.success(`✓ Digest sent to ${recipient} · provider id ${r.data.provider_id?.slice(0, 12)}…`);
      else toast.success(`Digest queued (${r.data.status}) — set RESEND_API_KEY to send for real`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  const openHtml = async () => {
    const w = window.open("", "_blank");
    try {
      const res = await api.get("/einvoice/digest/html", { responseType: "text" });
      w.document.write(res.data);
    } catch (e) { w?.close(); console.warn("[Digest] HTML preview failed:", e?.message); toast.error("Preview failed"); }
  };
  return (
    <div className="card-sharp p-5 space-y-3" data-testid="digest-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">📧 Compliance digest email</div>
          <div className="text-xs text-muted-foreground mt-1">Nightly summary of missing IRNs + invoices approaching their 24h cancel window.</div>
        </div>
      </div>
      {preview && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
            <div className="text-2xl font-bold text-amber-700" data-testid="digest-missing">{preview.counts.missing_irn}</div>
            <div className="text-[10px] uppercase text-amber-800">Missing IRN</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
            <div className="text-2xl font-bold text-blue-700" data-testid="digest-irn-window">{preview.counts.irn_window}</div>
            <div className="text-[10px] uppercase text-blue-800">IRN cancel window</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
            <div className="text-2xl font-bold text-emerald-700" data-testid="digest-ewb-window">{preview.counts.ewb_window}</div>
            <div className="text-[10px] uppercase text-emerald-800">EWB cancel window</div>
          </div>
        </div>
      )}
      <div className="flex gap-2 items-end">
        <div className="flex-1"><Label>Send digest to</Label><Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} className="rounded-sm mt-1 font-mono" placeholder="cfo@company.in" data-testid="digest-to" /></div>
        <Button variant="outline" onClick={openHtml} className="rounded-sm">Preview HTML</Button>
        <Button onClick={send} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="digest-send">{busy ? "Sending…" : "Send now"}</Button>
      </div>
      <div className="text-[10px] text-muted-foreground italic">💡 Wire a cron / scheduler (or Emergent-hosted job) to hit <span className="font-mono">POST /api/einvoice/digest/send</span> nightly at 22:00 IST.</div>
    </div>
  );
}

function BulkCancelPanel() {
  const [fromDate, setFromDate] = React.useState("");
  const [toDate, setToDate] = React.useState("");
  const [onlyTest, setOnlyTest] = React.useState(true);
  const [job, setJob] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const loadJob = async () => {
    try { const r = await api.get("/einvoice/bulk-cancel/status"); setJob(r.data); }
    catch { setJob(null); }
  };
  React.useEffect(() => { loadJob(); }, []);
  React.useEffect(() => {
    if (job?.status !== "running" && job?.status !== "queued") return;
    const t = setInterval(loadJob, 2000); return () => clearInterval(t);
  }, [job?.status]);
  const start = async () => {
    if (!fromDate || !toDate) return toast.error("Pick a date range");
    if (!window.confirm(`Cancel every IRN generated in the last 24h for invoices dated ${fromDate} → ${toDate}? This calls the live NIC IRP for each one.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/einvoice/bulk-cancel", { from_date: fromDate, to_date: toDate, reason_code: "2", reason_note: "Test batch cancellation", only_test_mode: onlyTest });
      setJob(r.data);
      toast.success(`Bulk-cancel started · ${r.data.id.slice(0, 8)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-sharp p-5 space-y-3 border-red-200" data-testid="bulk-cancel-panel">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-red-600">⚠️ Bulk IRN cancel (test batch)</div>
        <div className="text-xs text-muted-foreground mt-1">
          Cancel every IRN generated in the last 24h across a date range. Useful when a mistake batch was accidentally posted.
          Only IRNs younger than 24h can be cancelled (NIC hard limit).
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><Label>From invoice date</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-sm mt-1" data-testid="bc-from" /></div>
        <div><Label>To invoice date</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-sm mt-1" data-testid="bc-to" /></div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={onlyTest} onChange={(e) => setOnlyTest(e.target.checked)} data-testid="bc-only-test" />
            <span>Only when Sandbox key is <span className="font-mono">key_test_</span> (safety guard)</span>
          </label>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={start} disabled={busy || job?.status === "running"} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="bc-start">
          {job?.status === "running" ? "Cancelling…" : "Start bulk cancel"}
        </Button>
      </div>
      {job && job.status && job.status !== "none" && (
        <div className="bg-muted/40 rounded-sm p-3 text-xs space-y-2" data-testid="bc-status">
          <div className="flex items-center gap-3">
            <span className={`inline-block h-2 w-2 rounded-full ${job.status === "running" ? "bg-red-500 animate-pulse" : job.status === "finished" ? "bg-emerald-500" : job.status === "aborted" ? "bg-amber-500" : "bg-muted-foreground"}`}></span>
            <span className="font-mono uppercase">{job.status}</span>
            <span className="text-muted-foreground">·</span>
            <span>{job.processed || 0} / {job.total || 0}</span>
            <span className="text-emerald-600">✓ {job.succeeded || 0}</span>
            <span className="text-red-600">✗ {job.failed || 0}</span>
          </div>
          {job.error && <div className="text-amber-700 font-mono text-[11px]">{job.error}</div>}
          {job.total > 0 && (
            <div className="h-1.5 bg-muted rounded-sm overflow-hidden">
              <div className="h-full bg-red-500 transition-all" style={{ width: `${Math.min(100, (job.processed / job.total) * 100)}%` }}></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
